/**
 * DEFUSE S16 — single-select panel-read residual classification.
 *
 * The AUTHORITATIVE set of user-facing single-select bag keys whose displayed
 * value the typed `TypedPropertyDescriptor` path does NOT (yet) reproduce, per
 * kind. A residual key is one of:
 *   - a roster↔production NAMING divergence (no descriptor for that exact label),
 *   - a dedicated-command / graph subsystem row (viewport view-descriptor set),
 *   - a DUAL-SOURCE decoder whose descriptor `display` diverges from the
 *     single-select builder (Text "Justify" → typed "Baseline Left" vs
 *     single-select "LEFT"), or
 *   - a latent production convenience row (Block composite "Insertion point" /
 *     "Scale" derived from the coords).
 *
 * S16 wires the Properties panel single-select read off the descriptor set
 * (`resolveDisplay`) for every NON-residual, NON-deferred, descriptor-covered
 * key — sourcing those cells from the typed model instead of the string bag —
 * and keeps ONLY these residual keys sourced from the bag. As future slices add
 * descriptors (composite Block rows, the per-viewport view-descriptor subsystem,
 * a unified Justify decoder) this set can ONLY SHRINK; the S16 panel census gate
 * (`panelReadCutover.census.test.ts`) and the S15 descriptor census both pin it
 * by construction so a NEW uncovered production key fails instead of silently
 * growing the bag-sourced surface.
 *
 * This lives in the package (not apps/web) per ПРИОРИТЕТ #1: the classification
 * is a typed CAD-core fact both the command-engine descriptor census and the
 * apps/web panel projection consume from ONE source of truth (no re-declared
 * clone that could drift).
 */

import type { EntityType } from "@dxf-ai-editor/contracts";
import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

import type { TypedPropertyDescriptor } from "./types";

export const SINGLE_SELECT_BAG_RESIDUAL: Partial<
  Record<EntityType, readonly string[]>
> = {
  // Line/Point endpoint & location Z (DXF group 30/31) + the 3-D Line `Length`:
  // the COMPACT typed cache the panel resolves against (`getEntityById` →
  // `lineFromCompact` / `pointFromCompact`) reconstructs ONLY X/Y — it DROPS the Z
  // slot entirely (`compactEntityStore` FLOATS_PER_LINE = start.x/y, end.x/y,
  // thickness; POINT_FLOATS = x, y, rotation, thickness — no z). A typed read of an
  // ELEVATED line/point would therefore render `Start Z` / `End Z` / `Position Z` as
  // `0` and Line `Length` as the 2-D distance, OVERWRITING the bag's real elevation
  // / 3-D length. Kept bag-sourced until the compact stores carry Z — the descriptor
  // set still COVERS these (the S15 census pins display === bag at the planar
  // fixture), this is a panel READ-PATH graph-loss decision, not a coverage gap
  // (Codex S16 R2; same class as the Multileader Float32 / common-residual entries).
  Line: ["Start Z", "End Z", "Length"],
  Point: ["Position Z"],
  // LWPOLYLINE `plinegen` (DXF group 70 bit 128 → "Linetype generation"):
  // `lwpolylineFromCompact` (CompactLwpolylineStore) stores only width / elevation /
  // thickness + the closed bit + vertices — it does NOT restore `plinegen`, so a
  // typed read reconstructs `plinegen ?? false` and the descriptor would blank a
  // polyline the bag shows as "true". Kept bag-sourced until the compact store
  // carries the flag (Codex S16 R2).
  LWPOLYLINE: ["Linetype generation"],
  // ACAD_TABLE `Table style`: group 342 names the style by HANDLE; the NAME
  // lives on the TABLESTYLE object in the OBJECTS section, which this project
  // retains verbatim in `DrawingDocument.rawObjectGroups` rather than decoding
  // into a typed style table (typed TABLESTYLE records land with the
  // table-editing feature). The preview builder resolves the real name from
  // those groups (`resolveTableStyleName`) — never a hard-coded "Standard" —
  // but a descriptor resolves against a lone `DrawingEntity` and has no access
  // to them, so the row stays bag-sourced. Same graph-loss class as the
  // INSERT's BLOCK_RECORD-sourced rows above.
  Table: ["Table style"],
  // composite convenience rows (`x,y` / `x,y,z`) S16 derives from the coords, PLUS
  // the BLOCK_RECORD-sourced metadata rows: the `blockMeta` descriptors project
  // the AutoCAD DEFAULT ("", "0", "1", "Yes") because the INSERT entity has no
  // access to the `blockRecords` / drawing units `buildBlockPreview` reads — so a
  // non-default record (real Description / Block Unit / Allow-* flags) would be
  // OVERWRITTEN by the default. Kept bag-sourced until the block-definition graph
  // overlay is wired (S16 P2 #12; Codex S16 R1).
  //
  // `Hyperlink` is a NOT-YET-ROUTED per-insert override: the block preview builder
  // PRESERVES the inbound `properties["Hyperlink"]` (`buildBlockPreview`) because
  // its `blockHyperlinkMutator` side-effect is DELIBERATELY unregistered (Slice G;
  // typedPropertyWriter). The `Hyperlink` descriptor therefore has NO typed home —
  // it reads/displays `""` — so a typed read would BLANK a Block whose bag carries
  // a real hyperlink. Kept bag-sourced until the hyperlink mutator/typed home is
  // actually wired (Codex S16 R4 #3 — same not-yet-routed class as Multileader
  // "Attachment").
  Block: [
    "Insertion point",
    "Scale",
    "Hyperlink",
    "Description",
    "Block Unit",
    "Block Unit Factor",
    "Allow Exploding",
    "Allow Non-Uniform Scale",
  ],
  // Justify: single-select justifyFromCodes ("CENTER") vs draft justifyLabel
  // ("Baseline Center") — dual decoder, S16 unifies. Generation flags: no roster row.
  Text: ["Justify", "Generation flags"],
  // MText fill-detail + factor rows the roster names differently / omits.
  MText: [
    "Line spacing factor",
    "Background color",
    "Background transparency",
    "Fill box scale",
    "Extrusion Z",
  ],
  // "Attachment" = hardcoded "Top left" placeholder in buildMultileaderPreview
  // (not entity-derived). Arrow/Landing X/Y are `String(...)` FULL-precision bag
  // projections of the leader vertices / textLocation; the single-select typed
  // read resolves the entity through the Float32 compact snapshot (`getEntityById`),
  // which rounds those coords to a binary artifact (e.g. textLocation.x = 3.1415926
  // → 3.1415925…). Kept bag-sourced until the panel reads a full-precision typed
  // source (Codex S16 R1 — see SINGLE_SELECT_COMMON_BAG_RESIDUAL for the same class
  // on common props).
  // `Text angle relative to dim line` (DXF group 53 — ezdxf's real
  // `text_rotation`, this codebase's `textAngleRelativeToDimLine`) and `Text
  // mirrored` (MIRRTEXT glyph-handedness, render-only — AcDbDimension has no
  // dedicated group, so it is NOT DXF-persisted) are NEW production bag keys
  // `buildDimensionPreview` emits so the legacy/PDF render path sees the MIRROR
  // command's additive text-angle negation and glyph-handedness output
  // (mirrtext-dimension-mleader-attrib). Distinct from the EXISTING "Text
  // rotation" descriptor above, which reads `entity.textRotation` (DXF group 52
  // — ezdxf's `oblique`, confusingly named in this codebase). Neither new field
  // has a typed Properties-panel descriptor yet (that UI slice is a separate,
  // out-of-scope follow-up) — kept bag-sourced until one is added.
  Dimension: ["Text angle relative to dim line", "Text mirrored"],
  Multileader: ["Attachment", "Arrow X", "Arrow Y", "Landing X", "Landing Y"],
  // Shade plot = ViewportInspector command (label via shadePlotModeEnglishLabel,
  // apps/web); the rest = per-viewport view-descriptor subsystem (EPIC-VIEWPORT).
  Viewport: [
    "Shade plot",
    "Viewport status",
    "Frozen layers",
    "Viewport cannoscale name",
    "Viewport cannoscale factor",
    "Viewport anno all visible",
  ],
};

/**
 * KIND-INDEPENDENT residual — common numeric object props every kind's single-
 * select bag emits at FULL precision via `String(...)` that the compact typed
 * snapshot the panel resolves against (`getEntityById`) cannot reproduce:
 *   - `Thickness` (DXF 39) is stored Float32 in the compact cache, so a value
 *     with >3 fractional digits round-trips to a binary artifact — AND the bag's
 *     `String(...)` diverges from the descriptor's `formatNumber` (3-dp) anyway;
 *   - `Linetype Scale` (DXF 48) is NOT round-tripped through the compact cache at
 *     all, so `getEntityById` drops it → the descriptor would blank a row the bag
 *     shows as e.g. "2.5".
 * These are common to EVERY kind (not a per-kind roster fact), so they live in a
 * flat set rather than in `SINGLE_SELECT_BAG_RESIDUAL[kind]` — and `Linetype
 * Scale` is CONDITIONALLY emitted, so a per-kind entry would fail the residual-
 * honesty gate on a kind whose fixture omits it. Kept bag-sourced across every
 * kind until the panel reads a full-precision typed source (Codex S16 R1). The
 * descriptor set still COVERS them (the S15 census pins display === bag at fixture
 * precision) — this is a panel READ-PATH fidelity decision, not a coverage gap.
 */
export const SINGLE_SELECT_COMMON_BAG_RESIDUAL: ReadonlySet<string> = new Set([
  "Thickness",
  "Linetype Scale",
]);

/** true for a common numeric prop kept bag-sourced on EVERY kind (precision /
 *  compact-drop — see {@link SINGLE_SELECT_COMMON_BAG_RESIDUAL}). */
export function isSingleSelectCommonBagResidual(label: string): boolean {
  return SINGLE_SELECT_COMMON_BAG_RESIDUAL.has(label);
}

/**
 * COMPACT Float32 read-path residual — a NUMERIC descriptor whose typed value the
 * single-select panel resolves through the Float32 compact snapshot
 * (`getEntityById` → `lineFromCompact` / `pointFromCompact` / `circleFromCompact`
 * / …). Every compact store packs GEOMETRY / TRANSFORM scalars into a
 * `Float32Array` column (`store.floats`), so a full-precision production
 * coordinate (e.g. `12345678.123`, 8 significant digits) round-trips to a binary
 * artifact (`12345678`, Float32 ULP = 1 above 2²³) through that column. A typed
 * read would therefore VISIBLY change the value AND an apply/copy of the row
 * would write the ROUNDED coordinate. The string bag carries the full-precision
 * `formatNumber(...)` projection of the ORIGINAL (Float64) preview entity, so
 * these stay bag-sourced until the panel reads a full-precision typed source
 * (Codex S16 R3 P2 — same class as `SINGLE_SELECT_COMMON_BAG_RESIDUAL` Thickness).
 *
 * Classification (mirrors {@link resolveDisplay}'s projection):
 *   - `point-coord` / `number` — EVERY geometry / transform numeric (Start X,
 *     Center X, Radius, Rotation, Text height, angles, scale, …) is a Float32
 *     column, so the whole editorKind is a compact-numeric residual.
 *   - numeric `read-only-calc` with NO decoded-label `display` — a derived value
 *     projected via the default `formatNumber` path (Length / Area / Angle /
 *     Measurement / Circumference) is computed FROM those Float32 coords, so it
 *     diverges too. A calc WITH a `display` renders a decoded STRING (Dimension
 *     "Subtype" → "Linear", Text "Justify" → "CENTER"), and a calc reading a
 *     STRING / BOOLEAN (RevCloud "Creation mode", "Annotative", "Annotation
 *     scale") is lossless — both stay typed-sourced.
 *   - a `text` descriptor whose typed READ yields Float32-backed NUMERIC data,
 *     even though its display is a custom-formatted STRING (Codex S16 R4 #1/#2):
 *       • a SCALAR number formatted into the display — Viewport "Scale" is
 *         `height / viewHeight` (both `Float32` columns via `viewportFromCompact`)
 *         rendered through `formatViewportScale`, so near a scale-label boundary
 *         the label recomputed from the rounded compact ratio diverges from the
 *         full-precision bag label;
 *       • a serialized numeric / point ARRAY — LWPOLYLINE "Vertices"/"Bulges" and
 *         Spline "Control points"/"Fit points" are `Float32` compact buffers
 *         (`CompactLwpolylineStore.vertexData`, `CompactSplineStore.fit/
 *         controlPointData`), so `serializePoints`/`serializeNumbers` over the
 *         reconstructed array rounds each coordinate. "Knots"/"Weights" are
 *         `Float64` (lossless) but stay bag-sourced by the SAME don't-cherry-pick
 *         rule below rather than a per-column exception.
 *     A `text` read returning a STRING / enum / style name (Transparency, style
 *     refs, Text override) is lossless and stays typed-sourced.
 *
 * The lossless common attributes (Color / Layer / Linetype / Lineweight /
 * Transparency, enums, booleans, style names) are STRING-interned in the compact
 * store (`lookupString`), NOT Float32 — they remain typed-sourced (the S16 cutover).
 * Integer counts (Wipeout "Vertex count", Multileader "Leader count") are numeric
 * reads through the same compact path — byte-identical (a count < 2²⁴ is exact),
 * but kept bag-sourced by the SAME by-construction rule rather than cherry-picking
 * which numerics are "safe" (a large or fractional value would silently lose data).
 */
export function isSingleSelectCompactNumericResidual(
  descriptor: TypedPropertyDescriptor,
  entity: DrawingEntity,
): boolean {
  if (
    descriptor.editorKind === "point-coord" ||
    descriptor.editorKind === "number"
  ) {
    return true;
  }
  if (descriptor.editorKind === "read-only-calc") {
    // A decoded-label display renders a STRING, not a Float32 numeric.
    if (descriptor.display) return false;
    const raw = descriptor.typedFieldRef?.read(entity);
    return typeof raw === "number" && Number.isFinite(raw);
  }
  // A `text` descriptor whose custom display FORMATS a Float32-backed numeric
  // read — a scalar (Viewport "Scale") or a serialized numeric / point array
  // (Vertices / Bulges / Control points / Fit points / Knots / Weights). A string
  // / enum / style-name read is lossless (string-interned) → stays typed-sourced.
  if (descriptor.editorKind === "text") {
    return isFloat32BackedNumericRead(descriptor.typedFieldRef?.read(entity));
  }
  return false;
}

/** true for a typed read whose displayed value derives from the compact store's
 *  Float32 numeric columns — a finite scalar number, or ANY array (a serialized
 *  numeric / point-coordinate collection reconstructed from a `Float32`/`Float64`
 *  buffer). A string / boolean / enum read is NOT Float32-backed. Empty arrays
 *  count (the descriptor class is array-backed regardless of the current length,
 *  so the partition stays value-independent). */
function isFloat32BackedNumericRead(raw: unknown): boolean {
  if (typeof raw === "number") return Number.isFinite(raw);
  return Array.isArray(raw);
}

/** The residual bag key set for a kind (empty for a fully-covered kind). Does NOT
 *  include the kind-independent {@link SINGLE_SELECT_COMMON_BAG_RESIDUAL}; callers
 *  that partition panel rows must consult BOTH. */
export function singleSelectBagResidual(kind: EntityType): ReadonlySet<string> {
  return new Set(SINGLE_SELECT_BAG_RESIDUAL[kind] ?? []);
}

/**
 * CONDITIONAL single-select bag rows a real drawing surfaces that carry NO typed
 * `TypedPropertyDescriptor` yet (their subsystem is not in the descriptor model),
 * and that the MINIMAL one-per-kind census fixtures never emit — so they cannot
 * live in {@link SINGLE_SELECT_BAG_RESIDUAL} (whose command-engine honesty gate
 * checks a FIXED planar / non-annotative production-bag snapshot that omits them).
 *
 * All are legitimately bag-sourced in production TODAY: `buildTypedSingleSelectRows`
 * finds no descriptor for the label → `typedSourced === false` → the bag value flows
 * through unchanged. This predicate exists so the apps/web panel-read census can
 * JUSTIFY those bag cells (exercised via ELEVATED / TILTED / ANNOTATIVE / ARRAYED /
 * STYLED edge fixtures) instead of flagging them as an UNCLASSIFIED silent growth of
 * the bag surface. As descriptors land the recognised set can only SHRINK.
 * (Codex S16 R2 finding #3.)
 *
 *   - 3-D / non-default-OCS ELLIPSE geometry (`deriveEllipsePreviewProperties`
 *     "Center Z" / "Major axis Z" / "Extrusion X/Y/Z") — emitted only for a
 *     non-planar ellipse; `curves.ts` has NO Z/extrusion descriptor (the palette
 *     Z-edit arm is a separate Cat-C slice).
 *   - annotative "Creation scale" (`creationScaleName`) — emitted only by an
 *     annotative Text / MText / Hatch / Block; naming-divergent from the common
 *     "Annotation scale" descriptor, so no descriptor for THIS label.
 *   - associative-ARRAY membership ("Array group" / "Array mode" = `arrayGroupId` /
 *     `arrayMode`) — the ARRAY-command graph subsystem, emitted on ANY arrayed
 *     entity; no typed descriptor.
 *   - dimension-STYLE DIMVAR projection (`resolveDimVarProperties` "DIM…") — emitted
 *     only when a DIMENSION resolves a style; a style-table subsystem row with no
 *     entity field / descriptor.
 *   - SPLINE fit-tangent vectors (`buildSplinePreview` "Start tangent X/Y[/Z]" /
 *     "End tangent X/Y[/Z]") — emitted only when the spline carries a
 *     `startTangent` / `endTangent`; `curves.ts` has NO tangent descriptor (the
 *     tangent-edit arm is a separate slice), so no descriptor for these labels
 *     (Codex S16 R3 #2). The Z component (Codex local review, batch-5 follow-up
 *     round 3 — a 3-D fit-point tangent's Z is needed for the SAME full-XYZ-
 *     magnitude normalization `fitPointsToControlPoints` already applies) is
 *     the SAME conditional class as its X/Y siblings.
 *   - dynamic-BLOCK combined "Visibility" selector (`buildBlockPreview`) — the
 *     single master-visibility dropdown AutoCAD shows in Properties, emitted only
 *     for a dynamic block carrying a visibility parameter. Distinct from the
 *     per-parameter `Param: <name>` rows (dynamic-key pattern); this fixed label is
 *     a visibility-subsystem row with no static descriptor (Codex S16 R3 #3).
 */
export function isSingleSelectConditionalBagResidual(
  kind: EntityType,
  key: string,
): boolean {
  if (
    kind === "Ellipse" &&
    (key === "Center Z" ||
      key === "Major axis Z" ||
      key === "Extrusion X" ||
      key === "Extrusion Y" ||
      key === "Extrusion Z")
  ) {
    return true;
  }
  if (key === "Creation scale") return true;
  if (key === "Array group" || key === "Array mode") return true;
  if (kind === "Dimension" && /^DIM[A-Z0-9]+$/u.test(key)) return true;
  if (
    kind === "Spline" &&
    (key === "Start tangent X" ||
      key === "Start tangent Y" ||
      key === "Start tangent Z" ||
      key === "End tangent X" ||
      key === "End tangent Y" ||
      key === "End tangent Z")
  ) {
    return true;
  }
  if (kind === "Block" && key === "Visibility") return true;
  return false;
}
